/**
 * Choosing which of two labels to show, when Xero holds both.
 *
 * A Qatari practice keeping bilingual books puts the Arabic in an account's
 * Name and the English in its Description:
 *
 *     4030   إيرادات البرامج التدريبية المعتمدة
 *            Accredited training programme revenue
 *
 * So "show the Name" is right in Arabic and wrong in English, and "show the
 * Description" is the reverse. Neither field is reliably one language.
 *
 * Xero's own default accounts complicate it further. There the Name is already
 * English — "Advertising" — and the Description is a sentence explaining what
 * the account is for. Preferring Description in English would replace a name
 * with a paragraph.
 *
 * So the choice is made on the script the text is written in rather than on
 * which field it came from. Ask for the reader's script; take the Name when it
 * is in that script, the Description when it is not, and the Name regardless
 * when neither is — because a label in the wrong language is still better than
 * no label.
 */

/** Arabic, Arabic Supplement, Extended-A, and the presentation forms. */
const ARABIC = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/;

export function hasArabic(text: string): boolean {
  return ARABIC.test(text);
}

export function pickForLanguage(
  name: string | null | undefined,
  description: string | null | undefined,
  language: 'ar' | 'en',
): string {
  const primary = (name ?? '').trim();
  const secondary = (description ?? '').trim();

  if (!secondary) return primary;
  if (!primary) return secondary;

  const wantArabic = language === 'ar';
  const primaryMatches = hasArabic(primary) === wantArabic;
  if (primaryMatches) return primary;

  const secondaryMatches = hasArabic(secondary) === wantArabic;
  return secondaryMatches ? secondary : primary;
}
